import {query} from '@di-nxt/query';
import {useEffect, useRef, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useGotoMembers} from '../../scenes';
import {AuthTokens, Team, useAuthToken, useTeams} from '../../services';
import {ReturnUrlKey} from './useAuthNavigation';

/**
 * Tracks the selected team and deselects when authTokens are empty (logged out),
 * and selects when authTokens are set (logged in). When logging in this hook
 * will also navigate to either the returnUrl parameter, the current location,
 * or default to the courier page.
 *
 * This hook will trigger when auth tokens change, and will wait untill we
 * have a valid list of teams (if any) loaded.
 *
 * @param selectedTeam selected team state
 * @param setSelectedTeam update selected team state method
 */
export const useTeamNavigation = () => {
  // Selected team state
  const [selectedTeam, setSelectedTeam] = useState<Team>();

  const authTokens = useAuthToken();
  const prevAuthTokensRef = useRef<AuthTokens>();

  const teams = useTeams();
  const handleNavigateToMembers = useGotoMembers();
  const location = useLocation();
  const history = useHistory();

  // Handle navigate to first team if team is undefined
  useEffect(() => {
    // Update when we change auth tokens
    if (prevAuthTokensRef.current !== authTokens) {
      if (!authTokens && prevAuthTokensRef.current !== undefined) {
        // Logged out - let's just remove the selected team
        setSelectedTeam(undefined);
        prevAuthTokensRef.current = authTokens;
      } else if (
        authTokens &&
        prevAuthTokensRef.current === undefined &&
        teams.length > 0
      ) {
        // Logged in - set selected team and return to the previous
        // url or navigate to the default route couriers
        const querystring = new URLSearchParams(location.search);
        const returnUrl = querystring.get(ReturnUrlKey);
        if (returnUrl && returnUrl !== '/') {
          // We have a valid return url and can navigate directly to it
          history.replace(returnUrl);
        } else {
          // We need to check if we are comming from somewhere (refresh of page)
          // or just needs to navigate to the default page (couriers)
          const firstTeam = query(teams).first();
          if (firstTeam) {
            setSelectedTeam(firstTeam);
            if (
              location.pathname === '' ||
              location.pathname === '/' ||
              location.pathname.startsWith('/login')
            ) {
              handleNavigateToMembers(firstTeam.id);
            }
          }
        }
        prevAuthTokensRef.current = authTokens;
      }
    }
    // Make sure we have set the default team
    if (authTokens && selectedTeam === undefined && teams.length > 0) {
      const firstTeam = query(teams).first();
      if (firstTeam) {
        setSelectedTeam(firstTeam);
      }
    }
  }, [
    authTokens,
    handleNavigateToMembers,
    history,
    location,
    selectedTeam,
    setSelectedTeam,
    teams,
  ]);

  return {selectedTeam, setSelectedTeam};
};
